// Decisions happen where the thing is (docs/06 §4.4): rooms and table cards are clicked on the board,
// Action cards are played or discarded from your hand, permits are picked as tokens. The prompt bar
// only says what is being asked and offers Skip/Done; the rare multi-part choices (the Memo trade,
// the Clerical Error swap) keep a compact panel. Every prompt also has a "List" fallback so nothing
// depends on finding the right object on the board.
import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { obscureBoard } from '../scene/pacing';
import { COLORS, type AvailableActions, type CardId, type Color, type PendingDecision, type PlayerView } from '@bcm/rules';
import { useGame } from '../state/store';
import { audio } from '../audio/sfx';
import { C, COLOR_NAME, locationName } from '../game/content';
import { Card, Glyph } from './Card';
import { PermitToken, SeatEmblem } from './Icon';
import { TAP, TOUCH, pointer } from './Tooltip';

type Decide = (choice: object) => void;

function useDecide(d: PendingDecision): Decide {
  const command = useGame((s) => s.command);
  return (choice) => command({ t: 'decide', decision: d.id, choice } as Parameters<typeof command>[0]);
}

export function DecisionBar({ view, actions, discardSel, clearDiscard }: { view: PlayerView; actions: AvailableActions | null; discardSel: CardId[]; clearDiscard: () => void }) {
  const d = actions?.decisions[0];
  const busy = useGame((s) => s.busy || s.inFlight);
  if (!d || d.kind === 'draft') return null;
  return (
    <div className={`decision-bar frame ${busy ? 'is-busy' : ''} kind-${d.kind}`} role="dialog" aria-label="Decision">
      <DecisionBody key={d.id} d={d} view={view} discardSel={discardSel} clearDiscard={clearDiscard} />
    </div>
  );
}

function Prompt({ title, text, children }: { title: string; text?: string; children?: React.ReactNode }) {
  return (
    <div className="bar-row">
      <div className="bar-text">
        <div className="bar-title">{title}</div>
        {text && <div className="bar-sub">{text}</div>}
      </div>
      <div className="bar-actions">{children}</div>
    </div>
  );
}

function DecisionBody({ d, view, discardSel, clearDiscard }: { d: PendingDecision; view: PlayerView; discardSel: CardId[]; clearDiscard: () => void }) {
  const decide = useDecide(d);
  const [list, setList] = useState(false);
  const me = view.seats[d.seat]!;
  const listToggle = (
    <button className="small ghost" onClick={() => setList((l) => !l)} aria-expanded={list}>
      {list ? 'Hide list' : 'List'}
    </button>
  );
  switch (d.kind) {
    case 'choosePermits':
      return <PermitTokens title={d.count === 1 ? 'Take a permit' : `Take ${d.count} permits`} colors={d.colors} exact={d.count} onConfirm={(bag) => decide({ permits: bag })} />;
    case 'liquidate':
      return (
        <PermitTokens
          title="Liquidate permits"
          text={`Up to ${d.max}, for ${d.manaEach} mana each`}
          colors={[...COLORS]}
          max={d.max}
          limits={me.permits}
          confirmLabel={(n) => (n ? `Liquidate · +${n * d.manaEach} mana` : 'Keep all')}
          onConfirm={(bag) => decide({ permits: bag })}
        />
      );
    case 'discardActions':
      return (
        <Prompt title={d.reason === 'handLimit' ? 'Too many Action cards' : 'Discard'} text={`Select ${d.count} Action card${d.count > 1 ? 's' : ''} in your hand to discard`}>
          <span className="bar-count">
            {discardSel.length}/{d.count}
          </span>
          <button
            className="gilt-btn primary small"
            disabled={discardSel.length !== d.count}
            onClick={() => {
              decide({ cards: discardSel });
              clearDiscard();
            }}
          >
            Discard
          </button>
        </Prompt>
      );
    case 'playAction':
      return (
        <Prompt
          title={locationName(d.at ?? 'planning-office')}
          text={d.remaining === 1 ? 'Play 1 Action card from your hand' : `Play Action cards from your hand · ${d.remaining} left`}
        >
          <button className="gilt-btn small" onClick={() => decide({ skip: true })}>
            Done
          </button>
        </Prompt>
      );
    case 'buySpell':
      return (
        <>
          <Prompt
            title={d.remote ? 'The Night Market' : 'The Arcanium'}
            text={
              d.options.length
                ? `${TAP} a spell on the table to buy it${d.refresh !== undefined ? ', or refresh the display first' : ''}`
                : 'Nothing on display you can afford — refresh it, or skip'
            }
          >
            {d.options.length > 0 && listToggle}
            {d.refresh !== undefined && (
              <button
                className="gilt-btn small"
                onClick={() => decide({ refresh: true })}
                data-tip={`Refresh the display|Pay ${d.refresh} {MANA}: the 5 spells on display go to the discard and 5 new ones are revealed. Once per visit.`}
              >
                Refresh · {d.refresh} <Glyph kind="mana" />
              </button>
            )}
            <button className="gilt-btn small" onClick={() => decide({ skip: true })}>
              Skip
            </button>
          </Prompt>
          {list && (
            <div className="card-row bar-list">
              {d.options.map((o) => (
                <Card key={o.card} def={view.cards[o.card]!} view={view} size="sm" state="playable" badge={<span className="price-badge">Buy · {o.price}</span>} onClick={() => decide({ card: o.card })} />
              ))}
            </div>
          )}
        </>
      );
    case 'claimProblem':
      return (
        <>
          <Prompt title="The Grand Auditor" text={`${TAP} a City Problem on the table to claim it`}>
            {listToggle}
            <button className="gilt-btn small" onClick={() => decide({ skip: true })}>
              Skip
            </button>
          </Prompt>
          {list && (
            <div className="card-row bar-list">
              {d.options.map((o) => {
                const inMana = Object.values(o.manaPermits ?? {}).reduce((n, x) => n + (x ?? 0), 0) * 3;
                return (
                  <Card
                    key={o.problem}
                    def={o.problem}
                    size="sm"
                    state="playable"
                    badge={
                      <span className="price-badge" data-tip={inMana ? `Currency Reform|${inMana} mana pays the fee's missing permits.` : undefined}>
                        {o.vp} VP{inMana ? ` · ${inMana} mana` : ''}
                      </span>
                    }
                    onClick={() => decide({ problem: o.problem })}
                  />
                );
              })}
            </div>
          )}
        </>
      );
    case 'chooseLocation':
      return (
        <>
          <Prompt title={d.purpose === 'audit' ? 'Audit' : d.purpose === 'overtime' ? 'Overtime Shift' : 'Notarized Copy'} text={`${TAP} a lit room on the board`}>
            {listToggle}
          </Prompt>
          {list && (
            <div className="row wrap bar-list">
              {d.locations.map((l) => (
                <button key={l} className="small" onClick={() => decide({ location: l })}>
                  {locationName(l)}
                </button>
              ))}
            </div>
          )}
        </>
      );
    case 'exchange':
      return <ExchangePicker d={d} view={view} onConfirm={(c) => decide(c)} />;
    case 'swapSpell':
      return <SwapPicker d={d} view={view} onConfirm={(c) => decide(c)} />;
    case 'chooseSpell':
      return <AppealPicker d={d} view={view} onConfirm={(card) => decide({ card })} />;
    case 'chooseDecree':
      return (
        <div className="bar-panel">
          <div className="bar-title">Repeal a Decree</div>
          <div className="bar-sub">Strike one of the Decrees in force. Its author no longer scores it.</div>
          <div className="card-row">
            {d.decrees.map((id) => {
              const by = view.decrees?.find((x) => x.decree === id)?.by;
              return (
                <Card
                  key={id}
                  def={id}
                  size="sm"
                  state="playable"
                  onClick={() => decide({ decree: id })}
                  badge={
                    by === null ? (
                      <span className="owner-badge">The Ministry</span>
                    ) : by !== undefined ? (
                      <span className="owner-badge">
                        <SeatEmblem seat={by} size={16} /> {view.seats[by]!.name}
                      </span>
                    ) : undefined
                  }
                />
              );
            })}
          </div>
        </div>
      );
    // ── Annex rooms (P-18) ──
    case 'returnPermits':
      return (
        <PermitTokens
          title="The Exchange Bureau"
          text={`Return up to ${d.max} of your permits — then take as many of your choice`}
          colors={COLORS.filter((c) => me.permits[c] > 0)}
          max={d.max}
          limits={me.permits}
          confirmLabel={(n) => (n ? `Return ${n}` : 'Keep them')}
          onConfirm={(bag) => decide({ permits: bag })}
        />
      );
    case 'recoverSpell':
      return (
        <div className="bar-panel">
          <div className="bar-title">Lost Property</div>
          <div className="bar-sub">Take one spell from the discard into your hand.</div>
          <div className="card-row">
            {d.cards.map((c) => (
              <Card key={c} def={view.cards[c]!} view={view} size="sm" state="playable" onClick={() => decide({ card: c })} />
            ))}
          </div>
        </div>
      );
    case 'shred':
      return <ShredPicker d={d} view={view} onConfirm={(cards) => decide({ cards })} />;
    case 'giveAction':
      return <GivePicker d={d} view={view} onConfirm={(c) => decide(c)} />;
    case 'chooseRival':
      return (
        <Prompt title="Subpoena" text="Take a random Action card from…">
          {d.seats.map((seat) => (
            <button key={seat} className="seat-choice" onClick={() => decide({ seat })}>
              <SeatEmblem seat={seat} size={20} /> {view.seats[seat]!.name} · {view.seats[seat]!.actionHandCount} cards
            </button>
          ))}
        </Prompt>
      );
    default:
      return null;
  }
}

/** Big clickable permit tokens. A single pick confirms immediately; larger picks show a tally. */
function PermitTokens({
  title,
  text,
  colors,
  exact,
  max,
  limits,
  confirmLabel,
  onConfirm,
}: {
  title: string;
  text?: string;
  colors: Color[];
  exact?: number;
  max?: number;
  limits?: Record<Color, number>;
  confirmLabel?: (n: number) => string;
  onConfirm: (bag: Partial<Record<Color, number>>) => void;
}) {
  const [bag, setBag] = useState<Partial<Record<Color, number>>>({});
  const total = Object.values(bag).reduce((a, b) => a + (b ?? 0), 0);
  const cap = exact ?? max ?? 99;
  const ok = exact !== undefined ? total === exact : total <= cap;
  const add = (c: Color) => {
    if (exact === 1) return onConfirm({ [c]: 1 });
    if (total >= cap || (limits && (bag[c] ?? 0) >= limits[c])) return;
    setBag((b) => ({ ...b, [c]: (b[c] ?? 0) + 1 }));
  };
  const remove = (c: Color) => setBag((b) => ({ ...b, [c]: Math.max(0, (b[c] ?? 0) - 1) }));
  return (
    <div className="bar-row">
      <div className="bar-text">
        <div className="bar-title">{title}</div>
        {text && <div className="bar-sub">{text}</div>}
      </div>
      <div className="token-row">
        {colors.map((c) => {
          const n = bag[c] ?? 0;
          const disabled = exact !== 1 && (total >= cap || (limits ? n >= limits[c] : false));
          return (
            <button
              key={c}
              className={`permit-choice ${n ? 'picked' : ''}`}
              disabled={disabled && !n}
              onClick={() => add(c)}
              onContextMenu={(e) => {
                e.preventDefault();
                if (!pointer.touch) remove(c); // a long press on a phone shows the tip instead
              }}
              aria-label={`${COLOR_NAME[c]} permit${n ? `, ${n} chosen` : ''}`}
              data-tip={exact === 1 ? `${COLOR_NAME[c]} permit` : `${COLOR_NAME[c]}|${TOUCH ? 'Tap to add, tap the number to remove' : 'Click to add, right-click (or click the number) to remove'}`}
            >
              <PermitToken color={c} size={40} />
              {n > 0 && (
                <span
                  className="pick-count"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(c);
                  }}
                >
                  {n}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {exact !== 1 && (
        <div className="bar-actions">
          <span className="bar-count">
            {total}/{cap}
          </span>
          {total > 0 && (
            <button className="small ghost" onClick={() => setBag({})}>
              Reset
            </button>
          )}
          <button className="gilt-btn primary small" disabled={!ok} onClick={() => onConfirm(bag)}>
            {confirmLabel ? confirmLabel(total) : 'Confirm'}
          </button>
        </div>
      )}
    </div>
  );
}

function ExchangePicker({ d, view, onConfirm }: { d: Extract<PendingDecision, { kind: 'exchange' }>; view: PlayerView; onConfirm: (c: { target: number; give: Color; take: Color }) => void }) {
  const [target, setTarget] = useState(d.targets[0]!.seat);
  const [give, setGive] = useState<Color>(d.give[0]!);
  const t = d.targets.find((x) => x.seat === target)!;
  const [take, setTake] = useState<Color>(t.colors[0]!);
  const takeColor = t.colors.includes(take) ? take : t.colors[0]!;
  return (
    <div className="bar-panel">
      <div className="bar-title">Interdepartmental Memo</div>
      <div className="exchange-grid">
        <span className="bar-sub">With</span>
        <div className="row wrap">
          {d.targets.map((x) => (
            <button key={x.seat} className={`seat-choice ${x.seat === target ? 'is-on' : ''}`} onClick={() => setTarget(x.seat)}>
              <SeatEmblem seat={x.seat} size={20} /> {view.seats[x.seat]!.name}
            </button>
          ))}
        </div>
        <span className="bar-sub">You give</span>
        <div className="row">
          {d.give.map((c) => (
            <button key={c} className={`permit-choice small ${c === give ? 'picked' : ''}`} onClick={() => setGive(c)} aria-label={`Give ${COLOR_NAME[c]}`}>
              <PermitToken color={c} size={30} />
            </button>
          ))}
        </div>
        <span className="bar-sub">You take</span>
        <div className="row">
          {t.colors.map((c) => (
            <button key={c} className={`permit-choice small ${c === takeColor ? 'picked' : ''}`} onClick={() => setTake(c)} aria-label={`Take ${COLOR_NAME[c]}`}>
              <PermitToken color={c} size={30} />
            </button>
          ))}
        </div>
      </div>
      <div className="row">
        <button className="gilt-btn primary small" onClick={() => onConfirm({ target, give, take: takeColor })}>
          Send memo
        </button>
      </div>
    </div>
  );
}

function SwapPicker({ d, view, onConfirm }: { d: Extract<PendingDecision, { kind: 'swapSpell' }>; view: PlayerView; onConfirm: (c: { hand: CardId; arcanium: CardId }) => void }) {
  const [hand, setHand] = useState<CardId | null>(null);
  const [arc, setArc] = useState<CardId | null>(null);
  return (
    <div className="bar-panel">
      <div className="bar-title">Clerical Error</div>
      <div className="bar-sub">Give one spell from your hand…</div>
      <div className="card-row">
        {d.hand.map((c) => (
          <Card key={c} def={view.cards[c]!} view={view} size="sm" state={hand === c ? 'selected' : 'playable'} onClick={() => setHand(c)} />
        ))}
      </div>
      <div className="bar-sub">…for one from the Arcanium</div>
      <div className="card-row">
        {d.arcanium.map((c) => (
          <Card key={c} def={view.cards[c]!} view={view} size="sm" state={arc === c ? 'selected' : 'playable'} onClick={() => setArc(c)} />
        ))}
      </div>
      <div className="row">
        <button className="gilt-btn primary small" disabled={!hand || !arc} onClick={() => onConfirm({ hand: hand!, arcanium: arc! })}>
          Swap
        </button>
      </div>
    </div>
  );
}

/** The Shredding Room: pick up to `max` cards from either hand; each becomes a permit of your choice. */
function ShredPicker({ d, view, onConfirm }: { d: Extract<PendingDecision, { kind: 'shred' }>; view: PlayerView; onConfirm: (cards: CardId[]) => void }) {
  const [sel, setSel] = useState<CardId[]>([]);
  const toggle = (c: CardId) => {
    audio.play('select');
    setSel((s) => (s.includes(c) ? s.filter((x) => x !== c) : s.length < d.max ? [...s, c] : [...s.slice(1), c]));
  };
  const row = (cards: CardId[], label: string) =>
    cards.length > 0 && (
      <>
        <div className="bar-sub">{label}</div>
        <div className="card-row">
          {cards.map((c) => (
            <Card key={c} def={view.cards[c]!} view={view} size="sm" state={sel.includes(c) ? 'selected' : 'playable'} onClick={() => toggle(c)} />
          ))}
        </div>
      </>
    );
  return (
    <div className="bar-panel">
      <div className="bar-title">The Shredding Room</div>
      <div className="bar-sub">Discard up to {d.max} cards. Gain 1 permit of your choice for each.</div>
      {row(d.spells, 'Spells in hand')}
      {row(d.actions, 'Action cards')}
      <div className="row">
        <span className="bar-count">
          {sel.length}/{d.max}
        </span>
        <button className="gilt-btn primary small" onClick={() => onConfirm(sel)}>
          {sel.length ? `Shred ${sel.length} · +${sel.length} permit${sel.length > 1 ? 's' : ''}` : 'Shred nothing'}
        </button>
      </div>
    </div>
  );
}

/** The Mailroom: choose an Action card and the rival it goes to (only the two of you see which). */
function GivePicker({ d, view, onConfirm }: { d: Extract<PendingDecision, { kind: 'giveAction' }>; view: PlayerView; onConfirm: (c: { card: CardId; seat: number }) => void }) {
  const [card, setCard] = useState<CardId | null>(d.cards.length === 1 ? d.cards[0]! : null);
  const [seat, setSeat] = useState<number | null>(d.seats.length === 1 ? d.seats[0]! : null);
  return (
    <div className="bar-panel">
      <div className="bar-title">The Mailroom</div>
      <div className="bar-sub">Send one Action card to a rival. Only the two of you will see which.</div>
      <div className="card-row">
        {d.cards.map((c) => (
          <Card key={c} def={view.cards[c]!} view={view} size="sm" state={card === c ? 'selected' : 'playable'} onClick={() => setCard(c)} />
        ))}
      </div>
      <div className="row wrap">
        {d.seats.map((s) => (
          <button key={s} className={`seat-choice ${s === seat ? 'is-on' : ''}`} onClick={() => setSeat(s)}>
            <SeatEmblem seat={s} size={20} /> {view.seats[s]!.name}
          </button>
        ))}
        <button className="gilt-btn primary small" disabled={card === null || seat === null} onClick={() => onConfirm({ card: card!, seat: seat! })}>
          Send
        </button>
      </div>
    </div>
  );
}

/** Under Review / Revoke: rival spells grouped by owner; click one, then confirm. */
function AppealPicker({ d, view, onConfirm }: { d: Extract<PendingDecision, { kind: 'chooseSpell' }>; view: PlayerView; onConfirm: (card: CardId) => void }) {
  const [pick, setPick] = useState<CardId | null>(null);
  const owners = [...new Set(d.options.map((o) => o.seat))];
  const revoke = d.purpose === 'revoke';
  return (
    <div className="bar-panel">
      <div className="bar-title">{revoke ? 'Writ of Revocation' : 'File an Appeal'}</div>
      <div className="bar-sub">
        {revoke ? 'Return a rival’s spell to their hand. They lose its printed VP.' : 'Put a rival’s spell Under Review: its ability stops until the end of next round.'}
      </div>
      <div className="appeal-owners">
        {owners.map((seat) => (
          <div key={seat} className="appeal-owner">
            <div className="appeal-owner-name">
              <SeatEmblem seat={seat} size={18} /> {view.seats[seat]!.name} · {view.seats[seat]!.vp} VP
            </div>
            <div className="card-row">
              {d.options
                .filter((o) => o.seat === seat)
                .map((o) => (
                  <Card key={o.card} def={view.cards[o.card]!} view={view} size="sm" state={pick === o.card ? 'selected' : 'playable'} onClick={() => setPick(o.card)} />
                ))}
            </div>
          </div>
        ))}
      </div>
      <div className="row">
        <button className="gilt-btn primary small" disabled={!pick} onClick={() => onConfirm(pick!)}>
          {revoke ? 'Revoke' : 'Put Under Review'}
        </button>
      </div>
    </div>
  );
}

/**
 * Fly a copy of a card element into a HUD spot and fade it out there. Canvases don't clone their
 * pixels, so the card face is copied across by hand.
 */
function flyCardTo(el: Element | null | undefined, to: DOMRect | null, delay: number): Promise<void> {
  if (!(el instanceof HTMLElement) || !to) return Promise.resolve();
  const r = el.getBoundingClientRect();
  const clone = el.cloneNode(true) as HTMLElement;
  const src = el.querySelectorAll('canvas');
  clone.querySelectorAll('canvas').forEach((c, i) => c.getContext('2d')?.drawImage(src[i]!, 0, 0));
  Object.assign(clone.style, { position: 'fixed', left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, margin: '0', zIndex: '80', pointerEvents: 'none', transformOrigin: '50% 50%' });
  clone.classList.add('draft-flight');
  document.body.appendChild(clone);
  const dx = to.left + to.width / 2 - (r.left + r.width / 2);
  const dy = to.top + to.height / 2 - (r.top + r.height / 2);
  const scale = Math.max(0.12, Math.min(to.width / r.width, to.height / r.height));
  return new Promise((resolve) => {
    gsap
      .timeline({
        delay,
        onComplete: () => {
          clone.remove();
          resolve();
        },
      })
      .to(clone, { y: -24, scale: 1.06, duration: 0.18, ease: 'power2.out' })
      .to(clone, { x: dx, y: dy, scale, rotate: -4, duration: 0.62, ease: 'power3.inOut' })
      .to(clone, { opacity: 0, duration: 0.18 }, '-=0.12');
  });
}

export function DraftModal({ view, actions }: { view: PlayerView; actions: AvailableActions | null }) {
  const d = actions?.decisions.find((x) => x.kind === 'draft');
  const command = useGame((s) => s.command);
  const [identity, setIdentity] = useState<string | null>(null);
  const [agenda, setAgenda] = useState<string | null>(null);
  // The starting package's permits of your choice, picked with the Bureaucromancer (P-15).
  const [picks, setPicks] = useState<Color[]>([]);
  const [leaving, setLeaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // While the draft covers the board, the board idles at a low frame rate behind it; released as
  // soon as the draft starts fading so the board is back at full rate when it is revealed.
  const shown = !!d && d.kind === 'draft' && !leaving;
  const release = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!shown) return;
    release.current = obscureBoard();
    return () => release.current?.();
  }, [shown]);
  if (!d || d.kind !== 'draft' || leaving) {
    // Waiting on the others: a quiet note, not a wall — the table stays visible.
    if (view.phase === 'draft' && !d) {
      return (
        <div className="draft-waiting" role="status">
          <span className="loading-crest small" /> Awaiting the other appointments…
        </div>
      );
    }
    return null;
  }

  const pkg = identity ? C.identities.get(identity)?.package : undefined;
  const need = pkg?.choice ?? 0;

  /** The chosen cards fly into your panel, the draft fades, and only then is the choice filed. */
  const confirm = () => {
    const root = ref.current;
    if (!root) return;
    audio.play('stamp');
    const panel = document.querySelector('.my-panel')?.getBoundingClientRect() ?? null;
    const portrait = document.querySelector('[data-anchor="desk-identity"]')?.getBoundingClientRect() ?? panel;
    const agendaSpot = panel ? new DOMRect(panel.left + 12, panel.bottom - 150, panel.width - 24, 130) : null;
    const flights = [flyCardTo(root.querySelector('.draft-identities .is-selected'), portrait, 0), flyCardTo(root.querySelector('.draft-agendas .is-selected'), agendaSpot, 0.12)];
    release.current?.();
    gsap.to(root, { opacity: 0, duration: 0.35, ease: 'power1.in' });
    void Promise.all(flights).then(() => {
      setLeaving(true);
      const permits: Partial<Record<Color, number>> = {};
      for (const c of picks) permits[c] = (permits[c] ?? 0) + 1;
      command({ t: 'decide', decision: d.id, choice: { identity: identity!, agenda: agenda!, ...(picks.length ? { permits } : {}) } });
    });
  };

  return (
    <div className="modal-backdrop" ref={ref}>
      <div className="modal draft">
        <h2>Your appointment at the Ministry</h2>
        <div className="draft-cols">
          <section>
            <h3 className="section-title" data-tip="Bureaucromancer|Your character. Everyone sees who you picked.">
              Choose a Bureaucromancer
            </h3>
            <div className="card-row draft-identities">
              {d.identities.map((id) => (
                <Card
                  key={id}
                  def={id}
                  size="lg"
                  state={identity === id ? 'selected' : identity ? 'dim' : null}
                  onClick={() => {
                    if (id !== identity) setPicks([]);
                    setIdentity(id);
                  }}
                />
              ))}
            </div>
          </section>
          <section>
            <h3 className="section-title" data-tip="term:Secret Agenda">
              Choose a Secret Agenda
            </h3>
            <div className="card-row draft-agendas">
              {d.agendas.map((id) => (
                <Card key={id} def={id} size="lg" state={agenda === id ? 'selected' : agenda ? 'dim' : null} onClick={() => setAgenda(id)} />
              ))}
            </div>
          </section>
        </div>
        <div className="row draft-foot">
          {/* The package's permits of your choice are picked here, in the footer, so choosing a
              Bureaucromancer never shifts the cards above. */}
          {identity && need > 0 ? (
              <div className="package-pick">
                <span className="package-pick-label">
                  Starting package: choose {need} permit{need > 1 ? 's' : ''}
                </span>
                {COLORS.map((c) => {
                  const n = picks.filter((x) => x === c).length;
                  return (
                    <button
                      key={c}
                      className={`permit-choice ${n ? 'picked' : ''}`}
                      onClick={() => setPicks((p) => (p.length < need ? [...p, c] : [...p.slice(1), c]))}
                      aria-label={`${COLOR_NAME[c]} permit${n ? `, ${n} chosen` : ''}`}
                      data-tip={`${COLOR_NAME[c]}|${TAP} to add${need > 1 ? '; tap the number to remove' : ''}`}
                    >
                      <PermitToken color={c} size={34} />
                      {n > 0 && (
                        <span
                          className="pick-count"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPicks((p) => {
                              const i = p.lastIndexOf(c);
                              return i < 0 ? p : [...p.slice(0, i), ...p.slice(i + 1)];
                            });
                          }}
                        >
                          {n}
                        </span>
                      )}
                    </button>
                  );
                })}
                <span className="bar-count">
                  {picks.length}/{need}
                </span>
              </div>
          ) : (
            <span className="hint">
              {identity ? C.identities.get(identity)?.name : 'Pick a Bureaucromancer'} · {agenda ? C.agendas.get(agenda)?.name : 'pick an agenda'}
            </span>
          )}
          <button className="gilt-btn primary" disabled={!identity || !agenda || picks.length !== need} onClick={confirm}>
            Take the appointment
          </button>
        </div>
      </div>
    </div>
  );
}

export { Glyph };
